import React, { useState, useEffect } from 'react';
import { LinkIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/solid';
import { createTeamInvite, useTeamInvitesByOwner, deleteTeamInvite } from '../../util/db';
import { useAuth } from '../../util/auth';

function generateRandomHash() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

const InviteLinkModal = ({ isOpen, onClose }) => {
  const auth = useAuth();
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { data: existingInvites, refetch } = useTeamInvitesByOwner(auth.user?.uid);

  // Filter for active invites
  const activeInvites = existingInvites?.filter(inv => inv.active) || [];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setGeneratedLink('');
      setCopied(false);
    }
  }, [isOpen]);

  const handleGenerateLink = async () => {
    setIsGenerating(true);

    try {
      const hash = generateRandomHash();
      const link = `https://insuranceagencytraining.org/join/${hash}`;

      // Calculate expiration (30 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      // Create invite in database (now references ownerId instead of teamId)
      await createTeamInvite(hash, {
        ownerId: auth.user.uid,
        ownerName: auth.user.name || 'Team Owner',
        createdBy: auth.user.uid,
        expiresAt: expiresAt,
        useCount: 0,
        active: true,
        joinedUsers: []
      });

      setGeneratedLink(link);
      refetch(); // Refresh the list of invites
    } catch (error) {
      console.error('Error creating invite link:', error);
      alert('Failed to generate link. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link || generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy link. Please copy manually.');
    }
  };

  const handleDeactivate = async (hash) => {
    if (window.confirm('Deactivate this invite link? Anyone with the link will no longer be able to join.')) {
      try {
        await deleteTeamInvite(hash);
        refetch();
        if (generatedLink.includes(hash)) {
          setGeneratedLink('');
        }
      } catch (error) {
        console.error('Error deactivating link:', error);
        alert('Failed to deactivate link. Please try again.');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    const expiry = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    return new Date() > expiry;
  };

  if (!isOpen) return null;

  return (
    <div className="z-20 sm:fixed absolute inset-0 bg-black bg-opacity-75 flex justify-center items-center">
      <div className="bg-white p-8 rounded-lg shadow-lg lg:w-1/2 w-5/6 max-w-4xl relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-0 right-4 text-3xl text-gray-400 rounded-md"
        >
          &times;
        </button>

        <div className="flex flex-col text-center space-y-4">
          <LinkIcon className="h-12 text-green-700 mx-auto" />
          <h2 className="text-3xl font-bold">Invite Team Members</h2>
          <p className="text-gray-600">
            Generate a shareable link that lets people join your team directly.
            Share it via text, email, or in person.
          </p>

          {/* Generate New Link Button */}
          <button
            onClick={handleGenerateLink}
            disabled={isGenerating}
            className="bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 disabled:opacity-50"
          >
            {isGenerating ? 'Generating...' : 'Generate New Invite Link'}
          </button>

          {/* Newly Generated Link */}
          {generatedLink && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 mb-2 font-semibold">New link created!</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={generatedLink}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white"
                />
                <button
                  onClick={() => handleCopy()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-semibold ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {copied ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Copied!
                    </>
                  ) : (
                    'Copy'
                  )}
                </button>
              </div>
              <p className="text-xs text-green-700 mt-2">Valid for 30 days</p>
            </div>
          )}

          {/* Existing Active Links */}
          {activeInvites.length > 0 && (
            <div className="mt-6 text-left">
              <h3 className="font-semibold text-gray-700 mb-3">Active Invite Links</h3>
              <div className="space-y-2">
                {activeInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className={`p-3 rounded-lg border ${isExpired(invite.expiresAt) ? 'bg-gray-100 border-gray-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 truncate">
                          insuranceagencytraining.org/join/{invite.id}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Created: {formatDate(invite.createdAt)} |
                          Expires: {formatDate(invite.expiresAt)} |
                          {invite.useCount || 0} joined
                          {isExpired(invite.expiresAt) && <span className="text-red-500 ml-2">(Expired)</span>}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCopy(`https://insuranceagencytraining.org/join/${invite.id}`)}
                          className="text-green-600 hover:text-green-700 text-sm font-semibold"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => handleDeactivate(invite.id)}
                          className="text-red-500 hover:text-red-600"
                          title="Deactivate link"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-left mt-4">
            <h3 className="font-semibold text-gray-700 mb-2">How it works:</h3>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Click "Generate New Invite Link" to create a shareable link</li>
              <li>Copy and send the link to your team member (text, email, etc.)</li>
              <li>They click the link, sign up (if needed), and automatically join your team</li>
              <li>Links are valid for 30 days and can be used by multiple people</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteLinkModal;
